import React, { useEffect, useMemo, useState } from 'react';
import { createYearStructurePeriod, updateYearStructurePeriod } from '../services/apiService';
import AcademicYearService from '../services/AcademicYearService';
import {
    buildCalendarWeekYearOptions,
    buildEndKeyOptionsForStart,
    getDefaultNewPeriodState,
    getPeriodEditorStateFromPeriod,
    isValidEndForStart,
    normalizePeriodFromApi,
    parseYearWeekKey,
    validateNewPeriod
} from '../utils/yearStructurePeriodUtils';

const YearStructurePeriodEditor = ({
    variant = 'default',
    period = null,
    onSuccess,
    onCancel,
    setError,
    className = ''
}) => {
    const isEditMode = period != null;
    const [newPeriod, setNewPeriod] = useState(() => (
        isEditMode ? getPeriodEditorStateFromPeriod(period) : getDefaultNewPeriodState()
    ));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setNewPeriod(isEditMode ? getPeriodEditorStateFromPeriod(period) : getDefaultNewPeriodState());
    }, [period, isEditMode]);

    const calendarWeekYearOptions = useMemo(() => buildCalendarWeekYearOptions(), []);

    const endKeyOptionsForStart = useMemo(
        () => buildEndKeyOptionsForStart(newPeriod.startKey),
        [newPeriod.startKey]
    );

    const isCompact = variant === 'compact';

    const handleSavePeriod = async () => {
        const validation = validateNewPeriod(newPeriod);
        if (!validation.valid) {
            if (setError) setError(validation.error);
            return;
        }

        try {
            setSaving(true);
            if (setError) setError(null);

            const academicYearService = AcademicYearService.getInstance();
            let yearStructure;
            let savedPeriod;

            if (isEditMode) {
                const data = await updateYearStructurePeriod(period.id, validation.payload);
                yearStructure = data?.yearStructure;
                const periods = (yearStructure?.periods || []).map(normalizePeriodFromApi);
                savedPeriod = periods.find((p) => String(p.id) === String(period.id)) || null;
            } else {
                const data = await createYearStructurePeriod(validation.payload);
                yearStructure = data?.yearStructure;
                const periods = (yearStructure?.periods || []).map(normalizePeriodFromApi);
                savedPeriod = periods.find(
                    (p) => p.name === validation.payload.name
                        && (p.startWeek ?? p.start_week) === validation.payload.startWeek
                        && (p.endWeek ?? p.end_week) === validation.payload.endWeek
                ) || periods[periods.length - 1] || null;
                setNewPeriod(getDefaultNewPeriodState());
            }

            academicYearService.clearCache();

            if (onSuccess) {
                const periods = (yearStructure?.periods || []).map(normalizePeriodFromApi);
                onSuccess({
                    period: savedPeriod,
                    yearStructure: yearStructure ? { ...yearStructure, periods } : null
                });
            }
        } catch (err) {
            console.error('Error saving period:', err);
            if (setError) setError(`Failed to ${isEditMode ? 'update' : 'save'} period: ` + err.message);
        } finally {
            setSaving(false);
        }
    };

    const inputClassName = isCompact
        ? 'w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
        : 'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

    const selectClassName = isCompact
        ? 'w-full px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
        : 'w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

    const labelClassName = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div className={`${isCompact ? 'rounded-xl border border-gray-200 bg-gray-50 p-3' : 'bg-gray-50 p-4 rounded-lg'} ${className}`}>
            {!isCompact && (
                <h4 className="text-md font-medium text-gray-700 mb-3">
                    {isEditMode ? 'Edit Period' : 'Add New Period'}
                </h4>
            )}

            <div className={isCompact
                ? 'space-y-2.5'
                : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3 items-end'}>
                <input
                    type="text"
                    placeholder="Period name"
                    value={newPeriod.name}
                    onChange={(e) => setNewPeriod((prev) => ({ ...prev, name: e.target.value }))}
                    className={isCompact ? inputClassName : `${inputClassName} xl:col-span-2`}
                />

                <div className={isCompact ? '' : undefined}>
                    <label className={labelClassName}>Start (year · week)</label>
                    <select
                        value={newPeriod.startKey}
                        onChange={(e) => {
                            const nextStart = e.target.value;
                            setNewPeriod((prev) => {
                                const { year: sy, week: sw } = parseYearWeekKey(nextStart);
                                const { year: ey, week: ew } = parseYearWeekKey(prev.endKey);
                                let endKey = prev.endKey;
                                if (!sy || sw == null || Number.isNaN(sw)
                                    || !ey || ew == null || Number.isNaN(ew)
                                    || !isValidEndForStart(sy, sw, ey, ew)) {
                                    endKey = nextStart;
                                }
                                return { ...prev, startKey: nextStart, endKey };
                            });
                        }}
                        className={selectClassName}
                    >
                        {calendarWeekYearOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={isCompact ? '' : undefined}>
                    <label className={labelClassName}>End (year · week)</label>
                    <select
                        value={newPeriod.endKey}
                        onChange={(e) => setNewPeriod((prev) => ({ ...prev, endKey: e.target.value }))}
                        className={selectClassName}
                    >
                        {endKeyOptionsForStart.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`flex gap-2 ${isCompact ? '' : 'xl:col-span-2'}`}>
                    <input
                        type="color"
                        value={newPeriod.color}
                        onChange={(e) => setNewPeriod((prev) => ({ ...prev, color: e.target.value }))}
                        className="w-10 h-10 flex-none border border-gray-300 rounded-md"
                        aria-label="Period color"
                    />
                    <button
                        type="button"
                        onClick={handleSavePeriod}
                        disabled={saving}
                        className={`flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isCompact ? 'text-sm rounded-lg' : ''}`}
                    >
                        {saving ? 'Saving...' : (isEditMode ? 'Save' : 'Add')}
                    </button>
                </div>
            </div>

            {isCompact && onCancel && (
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={saving}
                    className="mt-2 w-full text-center text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
                >
                    Cancel
                </button>
            )}
        </div>
    );
};

export default YearStructurePeriodEditor;
